/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

function collectCartSections() {
  const sections = new Map();
  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }
    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;
      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;
      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;
      sections.set(sectionId, { mount, extractSelector: entry.selector || '.shopify-section' });
    }
  }
  return sections;
}

async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size ? `?sections=${[...sections.keys()].join(',')}` : '';
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

// Nachtcomfort: highlight only the ACTIES navigation link on desktop and mobile.
(() => {
  const style = document.createElement('style');
  style.id = 'nachtcomfort-actions-menu-red';
  style.textContent = '.header__inline-menu a[href="/collections/acties"],.menu-drawer a[href="/collections/acties"]{color:#c62828!important;}';
  document.head.appendChild(style);
})();

// Adore Signature: after Shopify rerenders variants on size change, re-run the Signature configurator.
(() => {
  const selector = 'variant-selects[data-product-handle="adore-signature-bedframe"]';
  document.addEventListener('change', (event) => {
    if (!event.target.closest(selector)) return;
    [80, 220, 500, 900, 1400].forEach((delay) => {
      setTimeout(() => document.dispatchEvent(new Event('shopify:section:load')), delay);
    });
  });
})();

// Adore Signature: keep the final steps clean and unique after rerenders.
(() => {
  const selector = 'variant-selects[data-product-handle="adore-signature-bedframe"]';
  const normalize = (value) => String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
  let cleaning = false;

  const clean = () => {
    if (cleaning) return;
    const picker = document.querySelector(selector);
    if (!picker) return;
    const productInfo = picker.closest('product-info') || document;
    cleaning = true;

    try {
      const totals = [...productInfo.querySelectorAll('.verdo-sticky-total')];
      const totalBar = totals[0] || null;
      totals.slice(1).forEach((el) => el.remove());

      const quantity = productInfo.querySelector('.product-form__quantity');
      const quantityLabel = quantity?.querySelector('.quantity__label,label');
      if (quantityLabel && quantityLabel.textContent.trim() !== '9. Aantal') {
        quantityLabel.textContent = '9. Aantal';
      }

      if (totalBar) {
        const totalLabel = totalBar.querySelector('small');
        if (totalLabel && totalLabel.textContent.trim() !== '10. Totaalprijs') {
          totalLabel.textContent = '10. Totaalprijs';
        }
      }

      const maintenanceLabel = productInfo.querySelector('[data-signature-maintenance="inclusive"] strong');
      if (maintenanceLabel && maintenanceLabel.textContent.trim() !== 'Inclusief onderhoudspakket + 5 jaar garantie') {
        maintenanceLabel.textContent = 'Inclusief onderhoudspakket + 5 jaar garantie';
      }

      const buy = productInfo.querySelector('.product-form__buttons');
      if (buy?.parentNode) {
        const parent = buy.parentNode;
        if (quantity && quantity.parentNode === parent && quantity.nextElementSibling !== totalBar) {
          parent.insertBefore(quantity, totalBar || buy);
        }
        if (totalBar && totalBar.parentNode === parent && totalBar.nextElementSibling !== buy) {
          parent.insertBefore(totalBar, buy);
        }
      }

      [...productInfo.querySelectorAll('label,legend')].forEach((el) => {
        if (el.closest('.verdo-options-flow')) return;
        if (/^montage(?:\s*\*)?$/.test(normalize(el.textContent)) && el.style.display !== 'none') {
          el.style.setProperty('display', 'none', 'important');
        }
      });
    } finally {
      cleaning = false;
    }
  };

  const run = () => [0, 60, 160, 350, 700, 1200, 1800].forEach((delay) => setTimeout(clean, delay));

  document.addEventListener('DOMContentLoaded', run);
  document.addEventListener('shopify:section:load', run);
  document.addEventListener('shopify:section:select', run);
  document.addEventListener('change', (event) => {
    if (event.target.closest(selector)) run();
  });

  const watch = () => {
    const picker = document.querySelector(selector);
    const productInfo = picker?.closest('product-info');
    if (!productInfo || productInfo.dataset.signatureFinalCleanupObserved === 'true') return;
    productInfo.dataset.signatureFinalCleanupObserved = 'true';
    let timer;
    const observer = new MutationObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(clean, 40);
    });
    observer.observe(productInfo, { childList: true, subtree: true });
    run();
  };

  [0, 300, 900, 1600].forEach((delay) => setTimeout(watch, delay));
})();

// All Adore products: permanently suppress the old legacy mattress option widget.
// The current Adore configurator remains the only customer-facing configurator.
(() => {
  const handles = new Set([
    'adore-signature-bedframe',
    'adore-designbed-met-opbergruimte',
    'adore-houston-met-opbergruimte',
    'adore-vana-met-opbergruimte',
    'adore-edira-met-opbergruimte',
    'adore-business-met-opbergruimte',
    'adore-chique-met-opbergruimte',
    'adore-melody-pearl-met-opbergruimte',
    'adore-element-pearl-met-opbergruimte',
    'adore-ruby-boucle-met-opbergruimte'
  ]);

  const match = location.pathname.match(/\/products\/([^/?#]+)/);
  const handle = match ? decodeURIComponent(match[1]) : '';
  if (!handles.has(handle)) return;

  const normalize = (value) => String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
  const legacyPattern = /(exclusief\s+matras|silver\s*[-–]?\s*line\s+pocketveer\s+matras)/i;
  let running = false;

  const removeLegacyMattressWidget = () => {
    if (running) return;
    running = true;
    try {
      const productInfo = document.querySelector('product-info') || document;
      const candidates = [...productInfo.querySelectorAll('label,legend,strong,span,p,div')].filter((el) => {
        if (el.closest('variant-selects[data-adore-configurator="true"],.signature-mattress-block,.verdo-options-flow')) return false;
        return legacyPattern.test(normalize(el.textContent));
      });

      candidates.forEach((el) => {
        let node = el;
        let target = null;
        for (let depth = 0; depth < 7 && node && node !== productInfo; depth += 1, node = node.parentElement) {
          const text = normalize(node.textContent);
          const controls = node.querySelectorAll?.('input[type="radio"],input[type="checkbox"],select')?.length || 0;
          if (legacyPattern.test(text) && controls >= 2 && text.length < 1200) {
            target = node;
            break;
          }
        }
        if (target && !target.closest('variant-selects[data-adore-configurator="true"],.signature-mattress-block,.verdo-options-flow')) {
          target.remove();
        }
      });
    } finally {
      running = false;
    }
  };

  const run = () => [0, 50, 120, 250, 500, 900, 1500, 2500].forEach((delay) => setTimeout(removeLegacyMattressWidget, delay));
  document.addEventListener('DOMContentLoaded', run);
  document.addEventListener('shopify:section:load', run);
  document.addEventListener('shopify:section:select', run);

  const observer = new MutationObserver(() => setTimeout(removeLegacyMattressWidget, 20));
  observer.observe(document.documentElement, { childList: true, subtree: true });
  run();
})();
